import { useEffect, useState } from "react";
import { createTeam, fetchSessionBrief } from "../api";
import type { SessionBrief, TeamRole } from "../types";

interface Props {
  sessionToken: string;
  onJoined: (teamToken: string, handle: string, teamRole: TeamRole) => void;
  onBack: () => void;
}

const ROLE_INFO: { id: TeamRole; label: string; tagline: string }[] = [
  { id: "ir-lead", label: "IR Lead", tagline: "Drives the timeline. Makes the call." },
  { id: "soc", label: "SOC Analyst", tagline: "Reads the logs. Plays the procedures." },
  { id: "comms", label: "Comms", tagline: "Briefs the CEO. Manages the press." },
];

export function JoinTeam({ sessionToken, onJoined, onBack }: Props) {
  const [brief, setBrief] = useState<SessionBrief>();
  const [briefErr, setBriefErr] = useState<string>();
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [teamName, setTeamName] = useState("");
  const [teamToken, setTeamToken] = useState("");
  const [handle, setHandle] = useState("");
  const [teamRole, setTeamRole] = useState<TeamRole>("ir-lead");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchSessionBrief(sessionToken)
      .then(setBrief)
      .catch(() => setBriefErr("Could not load this session — check the SESSION token."));
  }, [sessionToken]);

  const submit = async () => {
    setErr("");
    if (!handle.trim()) return setErr("pick a handle");
    if (mode === "new" && !teamName.trim()) return setErr("name your team");
    setBusy(true);
    try {
      let tk = teamToken.trim();
      if (mode === "new") {
        const r = await createTeam(sessionToken, teamName.trim());
        tk = r.teamToken;
      }
      onJoined(tk, handle.trim(), teamRole);
    } catch {
      setErr("could not join — check the token");
    } finally {
      setBusy(false);
    }
  };

  const phaseLabel = (() => {
    if (!brief) return "loading";
    if (brief.phase === "lobby") return "Waiting room open";
    if (brief.phase === "playing") return "In progress";
    return "Closed";
  })();

  return (
    <div className="row">
      <section className="panel grow" style={{ minWidth: 320 }}>
        <h2>Join a session</h2>
        {briefErr ? (
          <div className="token-warn">⚠ {briefErr}</div>
        ) : !brief ? (
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Loading…</div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--color-text-muted)",
                marginBottom: 14,
                flexWrap: "wrap",
              }}
            >
              <span style={{ color: "var(--color-accent)", fontWeight: 600 }}>{phaseLabel}</span>
              <span>·</span>
              <span>
                {brief.teams.length} team{brief.teams.length === 1 ? "" : "s"}
              </span>
              <span>·</span>
              <span>{brief.teams.reduce((a, t) => a + t.playerCount, 0)} players</span>
              <span>·</span>
              <span style={{ color: "var(--color-text-subtle)" }}>id {brief.sessionFingerprint}</span>
            </div>

            <p style={{ fontSize: 13, lineHeight: 1.6 }}>
              An incident is staged and waiting for blue teams. The Incident Master holds the brief
              — you'll get the premise once you're on a team and the room is set.
            </p>
            <p
              style={{
                fontSize: 12,
                lineHeight: 1.55,
                color: "var(--color-text-muted)",
                fontStyle: "italic",
                marginTop: 10,
              }}
            >
              Pick a handle. Pick a team — create a new one or join an existing team token your
              colleagues shared. Listen for the IM's narration when the game starts.
            </p>

            {brief.teams.length > 0 && (
              <div style={{ marginTop: 16, borderTop: "1px solid var(--color-border)", paddingTop: 12 }}>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.16em",
                    color: "var(--color-text-muted)",
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  Teams already here
                </div>
                {brief.teams.map((t) => (
                  <div
                    key={t.id}
                    style={{ fontFamily: "var(--font-mono)", fontSize: 12, padding: "2px 0" }}
                  >
                    {t.name}{" "}
                    <span style={{ color: "var(--color-text-muted)" }}>· {t.playerCount}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="panel sidebar">
        <h2>Team</h2>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button className={mode === "new" ? "primary" : ""} onClick={() => setMode("new")}>
            New team
          </button>
          <button className={mode === "existing" ? "primary" : ""} onClick={() => setMode("existing")}>
            Existing
          </button>
        </div>

        {mode === "new" ? (
          <input
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Team name (e.g. Blue Squad)"
            aria-label="Team name"
            maxLength={24}
          />
        ) : (
          <input
            type="text"
            value={teamToken}
            onChange={(e) => setTeamToken(e.target.value)}
            placeholder="TEAM-XXXX-XXXX-..."
            aria-label="Team token"
          />
        )}
        <div style={{ height: 8 }} />
        <input
          type="text"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="Your handle"
          aria-label="Your handle"
          maxLength={24}
        />

        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            color: "var(--color-text-subtle)",
            textTransform: "uppercase",
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          Your role on the team
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          {ROLE_INFO.map((r) => (
            <label
              key={r.id}
              style={{
                display: "block",
                border: `1px solid ${teamRole === r.id ? "var(--color-accent)" : "var(--color-border)"}`,
                background: teamRole === r.id ? "var(--color-bg-surface)" : "var(--color-bg)",
                padding: "8px 10px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="teamRole"
                value={r.id}
                checked={teamRole === r.id}
                onChange={() => setTeamRole(r.id)}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600 }}>{r.label}</span>
              <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", marginTop: 2 }}>{r.tagline}</div>
            </label>
          ))}
        </div>

        {err && <div className="token-warn">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={onBack}>← Back</button>
          <button className="primary" onClick={submit} disabled={busy || !brief}>
            {busy ? "…" : "Join"}
          </button>
        </div>
      </section>
    </div>
  );
}
